import { useMemo } from 'react';

import { createApi } from '@reduxjs/toolkit/query/react';

import { IAppFile } from '../../types/interfaces/app-file';
import { IExercise } from '../../types/interfaces/exercise';
import { IExerciseOption } from '../../types/interfaces/exercise-option';
import { ITraining } from '../../types/interfaces/training';
import { orderBy } from '../../utils/collections/array-utils';
import { API_CACHE_KEYS, ApiResponseCache } from '../api-response-cache';
import { EXERCISE_PAGES, HTTP_METHOD, REDUX_CACHE } from '../constants';
import { Exercise as ExercisePath } from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
} from '../generic';
import { useGetExerciseOptions } from './exerciseConfigAPI';
import {
  IExerciseListQueryArgs,
  buildExerciseListUrlParams,
  serializeExerciseListQueryArgs,
} from './exercises/exercise-list-query.types';
import {
  CopyExerciseRequest,
  IAvailableDetailedExerciseResponse,
  IAvailableExerciseResponse,
  IExerciseResultItem,
  IPublicCompactExercise,
} from './exercises/interfaces';
import {
  mapAvailableDetailedExerciseResponseToExercise,
  mapAvailableExerciseResponseToExercise,
} from './exercises/map-available-exercises';

export type { IExerciseListQueryArgs } from './exercises/exercise-list-query.types';

export interface IGetExerciseByIdArgs {
  id: string;
  fromTrainingId?: string;
  fromProgramId?: string;
}

function exerciseListPagedQuery(path: string, arg: IExerciseListQueryArgs) {
  return {
    url: `${path}?${getUrlParamsString(buildExerciseListUrlParams(arg))}`,
    method: HTTP_METHOD.GET,
  };
}

const transformErrorWithNotification =
  TransformedErrorResponseWithNotification().transformErrorResponse;
export const exercisesApi = createApi({
  reducerPath: 'exercisesApi',
  tagTypes: [
    EXERCISE_PAGES.CREATED_BY_USER,
    EXERCISE_PAGES.FAVORITE,
    EXERCISE_PAGES.PUBLIC,
    EXERCISE_PAGES.AVAILABLE,
    EXERCISE_PAGES.AVAILABLE_DETAILED,
    EXERCISE_PAGES.APPROVE_REQUESTED,
    EXERCISE_PAGES.ALL,
    EXERCISE_PAGES.ALL_FOR_ADDING,
    EXERCISE_PAGES.EXERCISE_RESULTS,
    EXERCISE_PAGES.EXERCISE_DETAIL,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getCreatedByUserExercisesApi: builder.query<
      IExercise[],
      IExerciseListQueryArgs | undefined
    >({
      keepUnusedDataFor: 3600,
      query(arg) {
        if (arg === undefined) {
          return {
            url: ExercisePath.CREATED_BY_USER,
            method: HTTP_METHOD.GET,
          };
        }
        return exerciseListPagedQuery(ExercisePath.CREATED_BY_USER, arg);
      },
      transformResponse: (response: IExercise[]) => orderBy(response, 'title'),
      providesTags: (result, _e, arg) =>
        arg === undefined
          ? getListProvidesTags(result, EXERCISE_PAGES.CREATED_BY_USER)
          : [
              {
                type: EXERCISE_PAGES.CREATED_BY_USER,
                id: 'paged_' + serializeExerciseListQueryArgs(arg),
              },
            ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getFavoriteExercisesApi: builder.query<
      IExercise[],
      IExerciseListQueryArgs | undefined
    >({
      keepUnusedDataFor: 3600,
      query(arg) {
        if (arg === undefined) {
          return {
            url: ExercisePath.FAVORITE,
            method: HTTP_METHOD.GET,
          };
        }
        return exerciseListPagedQuery(ExercisePath.FAVORITE, arg);
      },
      transformResponse: (response: IExercise[]) => orderBy(response, 'title'),
      providesTags: (result, _e, arg) =>
        arg === undefined
          ? getListProvidesTags(result, EXERCISE_PAGES.FAVORITE)
          : [
              {
                type: EXERCISE_PAGES.FAVORITE,
                id: 'paged_' + serializeExerciseListQueryArgs(arg),
              },
            ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getPublicExercisesApi: builder.query<
      IExercise[],
      IExerciseListQueryArgs | undefined
    >({
      keepUnusedDataFor: 3600,
      query(arg) {
        if (arg === undefined) {
          return {
            url: ExercisePath.PUBLIC,
            method: HTTP_METHOD.GET,
          };
        }
        return exerciseListPagedQuery(ExercisePath.PUBLIC, arg);
      },
      transformResponse: (response: IExercise[]) => orderBy(response, 'title'),
      providesTags: (result, _e, arg) =>
        arg === undefined
          ? getListProvidesTags(result, EXERCISE_PAGES.PUBLIC)
          : [
              {
                type: EXERCISE_PAGES.PUBLIC,
                id: 'paged_' + serializeExerciseListQueryArgs(arg),
              },
            ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getPublicCompactExercisesApi: builder.query<IPublicCompactExercise[], void>(
      {
        keepUnusedDataFor: 3600,
        query() {
          return {
            url: ExercisePath.PUBLIC_COMPACT,
            method: HTTP_METHOD.GET,
          };
        },
        transformResponse: (response: IPublicCompactExercise[]) =>
          orderBy(response, 'title'),
        providesTags: (result) =>
          getListProvidesTags(result, EXERCISE_PAGES.PUBLIC),
        ...TransformedErrorResponseWithNotification(),
      },
    ),
    getAvailableExercisesApi: builder.query<IAvailableExerciseResponse[], void>(
      {
        keepUnusedDataFor: 3600,
        query() {
          return {
            url: ExercisePath.AVAILABLE,
            method: HTTP_METHOD.GET,
          };
        },
        transformResponse: (response: IAvailableExerciseResponse[]) => {
          const sortedResponse = orderBy(response, 'title');
          ApiResponseCache.set(
            API_CACHE_KEYS.AVAILABLE_EXERCISES,
            sortedResponse,
          );
          return sortedResponse;
        },
        providesTags: (result) =>
          getListProvidesTags(result, EXERCISE_PAGES.AVAILABLE),
        ...TransformedErrorResponseWithNotification(),
        transformErrorResponse(
          response: { status?: number | string },
          meta: unknown,
        ) {
          if (response.status !== 'FETCH_ERROR') {
            ApiResponseCache.remove(API_CACHE_KEYS.AVAILABLE_EXERCISES);
          }

          return transformErrorWithNotification(response, meta);
        },
      },
    ),
    getAvailableDetailedExercisesApi: builder.query<
      IAvailableDetailedExerciseResponse[],
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: ExercisePath.AVAILABLE_DETAILED,
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: (response: IAvailableDetailedExerciseResponse[]) => {
        const sortedResponse = orderBy(response, 'title');
        ApiResponseCache.set(
          API_CACHE_KEYS.AVAILABLE_DETAILED_EXERCISES,
          sortedResponse,
        );
        return sortedResponse;
      },
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_PAGES.AVAILABLE_DETAILED),
      ...TransformedErrorResponseWithNotification(),
      transformErrorResponse(
        response: { status?: number | string },
        meta: unknown,
      ) {
        if (response.status !== 'FETCH_ERROR') {
          ApiResponseCache.remove(API_CACHE_KEYS.AVAILABLE_DETAILED_EXERCISES);
        }

        return transformErrorWithNotification(response, meta);
      },
    }),
    getAllForAddingExercisesApi: builder.query<
      IExercise[],
      IExerciseListQueryArgs
    >({
      keepUnusedDataFor: 3600,
      query(arg) {
        return exerciseListPagedQuery(ExercisePath.ALL_FOR_ADDING, arg);
      },
      transformResponse: (response: IExercise[]) => orderBy(response, 'title'),
      providesTags: (result, _, arg) => [
        {
          type: EXERCISE_PAGES.ALL_FOR_ADDING,
          id: 'allForAdding_' + serializeExerciseListQueryArgs(arg),
        },
      ],
    }),
    getExerciseByIdApi: builder.query<
      { exercise: IExercise; trainings: ITraining[] },
      IGetExerciseByIdArgs
    >({
      keepUnusedDataFor: 3600,
      query({ id, fromTrainingId, fromProgramId }) {
        const params: Record<string, string> = {};
        if (fromTrainingId) {
          params.fromTrainingId = fromTrainingId;
        }
        if (fromProgramId) {
          params.fromProgramId = fromProgramId;
        }
        const qs =
          Object.keys(params).length > 0
            ? `?${getUrlParamsString(params)}`
            : '';

        return {
          url: `${ExercisePath.ROOT}/${id}${qs}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (_result, _error, arg) => [
        { type: EXERCISE_PAGES.EXERCISE_DETAIL, id: arg.id },
      ],
      // ...TransformedErrorResponseWithNotification(),
    }),
    postPutExerciseApi: builder.mutation<
      { id: string; status: number },
      {
        id: string;
        body: {
          exercise: Partial<
            IExercise & {
              avatarFileId: string | number;
              additionalEquipmentIds?: string[];
              additionalMuscleIds?: string[];
              developedSkillIds?: string[];
            }
          >;
          filesToUpload?: IAppFile[];
          fileIdsToDelete?: string[];
        };
      }
    >({
      query({ id, body }) {
        return {
          url: ExercisePath.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }
        const exerciseId = params.id || result?.id;
        const tags: Array<{
          type: (typeof EXERCISE_PAGES)[keyof typeof EXERCISE_PAGES];
          id: string;
        }> = [
          {
            type: EXERCISE_PAGES.CREATED_BY_USER,
            id: REDUX_CACHE.LIST,
          },
          {
            type: EXERCISE_PAGES.AVAILABLE,
            id: REDUX_CACHE.LIST,
          },
          {
            type: EXERCISE_PAGES.AVAILABLE_DETAILED,
            id: REDUX_CACHE.LIST,
          },
        ];
        if (exerciseId) {
          tags.push(
            {
              type: EXERCISE_PAGES.CREATED_BY_USER,
              id: exerciseId,
            },
            {
              type: EXERCISE_PAGES.EXERCISE_DETAIL,
              id: exerciseId,
            },
          );
        }
        return tags;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    copyExerciseApi: builder.mutation<
      void,
      {
        id: string;
        body: CopyExerciseRequest;
      }
    >({
      query({ id, body }) {
        return {
          url: ExercisePath.COPY(id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.CREATED_BY_USER,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.AVAILABLE,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.AVAILABLE_DETAILED,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    replaceExerciseWithPublicApi: builder.mutation<
      boolean,
      {
        id: string;
        body: {
          replacementExerciseId: string;
        };
      }
    >({
      query({ id, body }) {
        return {
          url: ExercisePath.REPLACE_WITH_PUBLIC(id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, { id, body }) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }
        const tags = [
          {
            type: EXERCISE_PAGES.CREATED_BY_USER,
            id: REDUX_CACHE.LIST,
          },
          {
            type: EXERCISE_PAGES.AVAILABLE,
            id: REDUX_CACHE.LIST,
          },
          {
            type: EXERCISE_PAGES.AVAILABLE_DETAILED,
            id: REDUX_CACHE.LIST,
          },
          { type: EXERCISE_PAGES.EXERCISE_DETAIL, id },
          {
            type: EXERCISE_PAGES.EXERCISE_DETAIL,
            id: body.replacementExerciseId,
          },
        ];
        return tags;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    exercisePublicRequestApi: builder.mutation<
      void,
      { exerciseId: string; canCopy: boolean }
    >({
      query({ exerciseId, canCopy }) {
        return {
          url: ExercisePath.PUBLIC_REQUEST(exerciseId),
          method: HTTP_METHOD.PUT,
          body: { canCopy },
        };
      },
      invalidatesTags: (result, error, { exerciseId }) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.CREATED_BY_USER,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.APPROVE_REQUESTED,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.CREATED_BY_USER,
              id: exerciseId,
            },
            {
              type: EXERCISE_PAGES.EXERCISE_DETAIL,
              id: exerciseId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    exerciseCancelPublicRequestApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: ExercisePath.CANCEL_PUBLIC_REQUEST(id),
          method: HTTP_METHOD.PUT,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.CREATED_BY_USER,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.APPROVE_REQUESTED,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_PAGES.CREATED_BY_USER,
              id,
            },
            {
              type: EXERCISE_PAGES.EXERCISE_DETAIL,
              id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteExerciseApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: `${ExercisePath.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.CREATED_BY_USER,
              id,
            },
            {
              type: EXERCISE_PAGES.EXERCISE_DETAIL,
              id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    addUserExerciseApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: `${ExercisePath.ROOT}/${id}${ExercisePath.ADD}`,
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_PAGES.ALL_FOR_ADDING,
              id,
            },
            {
              type: EXERCISE_PAGES.FAVORITE,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getExerciseResultsApi: builder.query<IExerciseResultItem[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: ExercisePath.RESULTS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_PAGES.EXERCISE_RESULTS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getExerciseResultsForUserApi: builder.query<IExerciseResultItem[], string>({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: ExercisePath.RESULTS_FOR_USER(userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, _error, userId) => [
        ...getListProvidesTags(result, EXERCISE_PAGES.EXERCISE_RESULTS),
        { type: EXERCISE_PAGES.EXERCISE_RESULTS, id: `user-${userId}` },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const resetExerciseApiState = () => exercisesApi.util.resetApiState();

export const {
  useGetCreatedByUserExercisesApiQuery: useGetCreatedByUserExercises,
  useGetFavoriteExercisesApiQuery: useGetFavoriteExercises,
  useGetPublicExercisesApiQuery: useGetPublicExercises,
  useGetPublicCompactExercisesApiQuery: useGetPublicCompactExercises,
  useGetAvailableExercisesApiQuery,
  useGetAvailableDetailedExercisesApiQuery,
  useLazyGetAllForAddingExercisesApiQuery: useLazyGetAllForAddingExercises,
  useLazyGetPublicExercisesApiQuery: useLazyGetPublicExercises,
  useLazyGetCreatedByUserExercisesApiQuery: useLazyGetCreatedByUserExercises,
  useLazyGetFavoriteExercisesApiQuery: useLazyGetFavoriteExercises,
  useLazyGetExerciseByIdApiQuery: useLazyGetExerciseById,
  useGetExerciseByIdApiQuery: useGetExerciseById,
  usePostPutExerciseApiMutation: usePostPutExercise,
  useExercisePublicRequestApiMutation: useExercisePublicRequest,
  useExerciseCancelPublicRequestApiMutation: useExerciseCancelPublicRequest,
  useDeleteExerciseApiMutation: useDeleteExercise,
  useAddUserExerciseApiMutation: useAddUserExercise,
  useCopyExerciseApiMutation: useCopyExercise,
  useReplaceExerciseWithPublicApiMutation: useReplaceExerciseWithPublic,
  useLazyGetExerciseResultsApiQuery: useLazyGetExerciseResults,
  useGetExerciseResultsApiQuery: useGetExerciseResults,
  useGetExerciseResultsForUserApiQuery: useGetExerciseResultsForUser,
} = exercisesApi;

type AvailableExerciseQueryResult =
  | ReturnType<typeof useGetAvailableExercisesApiQuery>
  | ReturnType<typeof useGetAvailableDetailedExercisesApiQuery>;

type NormalizedAvailableExercisesResult = Omit<
  AvailableExerciseQueryResult,
  'data' | 'isLoading' | 'isFetching'
> & {
  data: IExercise[];
  isPreloadedCached: boolean;
  isLoading: boolean;
  isFetching: boolean;
};

function useNormalizedAvailableExercises<T extends IAvailableExerciseResponse>(
  queryResult: AvailableExerciseQueryResult,
  cacheKey:
    | typeof API_CACHE_KEYS.AVAILABLE_EXERCISES
    | typeof API_CACHE_KEYS.AVAILABLE_DETAILED_EXERCISES,
  mapItem: (item: T, exerciseOptions: IExerciseOption[]) => IExercise,
): NormalizedAvailableExercisesResult {
  const optionsState = useGetExerciseOptions();
  const cachedList = ApiResponseCache.get<T[]>(cacheKey);
  const raw = (queryResult.data ?? cachedList) as T[] | undefined;

  const resolvedOptions =
    optionsState.data ??
    ApiResponseCache.get<IExerciseOption[]>(API_CACHE_KEYS.EXERCISE_OPTIONS) ??
    (optionsState.isError ? [] : undefined);

  const exercisesBlocking = queryResult.isLoading || queryResult.isFetching;
  const optionsBlocking =
    resolvedOptions === undefined &&
    !optionsState.isError &&
    (optionsState.isLoading || optionsState.isFetching);

  const isReady = raw !== undefined && !exercisesBlocking && !optionsBlocking;

  const data = useMemo(() => {
    if (!isReady || raw === undefined) {
      return [];
    }
    return raw.map((item) => mapItem(item, resolvedOptions ?? []));
  }, [isReady, raw, resolvedOptions, mapItem]);

  const isPreloadedCached =
    !queryResult.data &&
    !!cachedList &&
    !!(
      optionsState.data ??
      ApiResponseCache.get<IExerciseOption[]>(API_CACHE_KEYS.EXERCISE_OPTIONS)
    );

  const waitingForNormalizedData =
    !queryResult.isError && !optionsState.isError && !isReady;

  return {
    ...queryResult,
    data,
    isPreloadedCached,
    isLoading: queryResult.isError
      ? queryResult.isLoading
      : waitingForNormalizedData,
    isFetching:
      queryResult.isFetching ||
      optionsState.isFetching ||
      waitingForNormalizedData,
  } as NormalizedAvailableExercisesResult;
}

export const useGetAvailableExercises = () => {
  const queryResult = useGetAvailableExercisesApiQuery(undefined);
  return useNormalizedAvailableExercises(
    queryResult,
    API_CACHE_KEYS.AVAILABLE_EXERCISES,
    mapAvailableExerciseResponseToExercise,
  );
};

export const useGetAvailableDetailedExercises = () => {
  const queryResult = useGetAvailableDetailedExercisesApiQuery(undefined);
  return useNormalizedAvailableExercises<IAvailableDetailedExerciseResponse>(
    queryResult,
    API_CACHE_KEYS.AVAILABLE_DETAILED_EXERCISES,
    mapAvailableDetailedExerciseResponseToExercise,
  );
};
